import {ChangeDetectorRef, Component, forwardRef, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {SessionActions} from "../../../shared/actions/session.actions";
import {Medicine} from "../../../store-management/model/medicine.type";
import {ApolloQueryObservable} from "apollo-angular";
import {DragulaService} from "ng2-dragula";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AbstractVisbosControl} from "../../../core/visbos-core/ui/forms/abstract-visbos-control";
import {PrintedSheet} from "../../../shared/view-model/store-management/printed-sheet.type";
import {StickerPageComponent} from "./sticker-page/sticker-page.component";
import {MessageType} from "../../../shared/redux-store/session/session.types";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {StickerSheet} from "../../../shared/view-model/store-management/sticker-sheet.type";
import {appInjector} from "../../../index";
import {StickerFormat} from "../../../shared/view-model/store-management/sticker-format.type";
import {
  MedicineStickersService,
  PrintedSheetsResult,
  StickerFormatsResult
} from "../../services/medicine-stickers.service";
import {MedicineService} from "../../../store-management/services/medicine.service";

import {PrintedSheetCategory} from "../../model/printed-sheets.enum";
import {ActivatedRoute, Router} from "@angular/router";

@Component({
  selector: 'vb-custom-sticker-page-layout',
  templateUrl: './custom-sticker-page-layout.component.html',
  styleUrls: ['./custom-sticker-page-layout.component.css'],
  providers: [{provide: AbstractVisbosControl, useExisting: forwardRef(() => CustomStickerPageLayoutComponent)}]
})
export class CustomStickerPageLayoutComponent extends AbstractVisbosControl<StickerSheet, FormGroup> implements OnInit, OnDestroy {

  private medDrake: any;

  private medicineList$: ApolloQueryObservable<any>;
  medicineList: Medicine[] = [];
  origMedicineList: Medicine[] = [];

  private printedSheetsList$: ApolloQueryObservable<PrintedSheetsResult>;
  printedSheetsList: PrintedSheet[] = [];

  private stickerFormatList$: ApolloQueryObservable<StickerFormatsResult>;
  stickerFormatList: StickerFormat[] = [];

  searchTerm: FormControl = new FormControl();

  stickerSheetCompleted: boolean = false;

  @ViewChild('stickerPage')
  stickerPage: StickerPageComponent;

  previewImages: { [medicineCuid: string]: string } = {};

  private printedSheetCuid$: BehaviorSubject<string>;
  private stickerFormatCuid$: BehaviorSubject<string>;

  PrintedSheetCategory;

  filterItemProxy = (searchTerm: string) => this.filterItems.apply(this, [searchTerm]);

  constructor(fb: FormBuilder, cd: ChangeDetectorRef, sessionActions: SessionActions,
              private router: Router, private route: ActivatedRoute,
              private medStickerService: MedicineStickersService, private medService: MedicineService, private dragulaService: DragulaService) {
    super(fb, cd, sessionActions);

    this.PrintedSheetCategory = PrintedSheetCategory;

    this.dragulaService.setOptions('medicines', {
      copy: (el, source) => { // Only copy, when moving from source container
        return source.id == 'medicineListContainer'
      },
      accepts: (el, target) => { // Prevent moving/re-ordering in source container
        return target.id != 'medicineListContainer'
      },
      //removeOnSpill: true, // Delete items when dropped outside of target bag
      revertOnSpill: true,
    });

  }

  buildControlContent(values?: StickerSheet, ...params): FormGroup {
    return CustomStickerPageLayoutComponent.initControl(this.fb);
  }

  public static initControl(fb: FormBuilder = null): FormGroup {
    fb = fb ? fb : appInjector().get(FormBuilder);

    return fb.group({
      cuid: [null],
      label: [null],
      administration_unit_id: [null, Validators.required],
      printed_sheet_id: [null, Validators.required],
      sticker_format_id: [null, Validators.required]
    });

  }

  public setDefaultValues(item?: StickerSheet): { [controlKey: string]: any } | any {
    return {
      cuid: this.setValue(item, 'cuid', AbstractVisbosControl.cuid()),
      label: this.setValue(item, 'label'),
      administration_unit_id: this.setValue(item, 'administration_unit.cuid', this.activeAdminUnitCuid),
      printed_sheet_id: this.setValue(item, 'printed_sheet.cuid'),
      sticker_format_id: this.setValue(item, 'sticker_format.cuid')
    };
  }

  ngOnInit() {
    super.ngOnInit();

    // Form changes
    let ps = this.control.get('printed_sheet_id');
    let sf = this.control.get('sticker_format_id');

    this.printedSheetCuid$ = new BehaviorSubject(ps.value);
    this.stickerFormatCuid$ = new BehaviorSubject(sf.value);

    this.dataSubscriptions.push(ps.valueChanges.subscribe(val => this.printedSheetCuid$.next(val)));
    this.dataSubscriptions.push(sf.valueChanges.subscribe(val => this.stickerFormatCuid$.next(val)));

    this.medDrake = this.dragulaService.find('medicines').drake;

    this.printedSheetsList$ = this.medStickerService.getPrintedSheets();
    this.addComponentData(this.printedSheetsList$);

    this.dataSubscriptions.push(this.printedSheetsList$.subscribe(res => {
      this.printedSheetsList = res.data.printedSheets;
    }));

    this.stickerFormatList$ = this.medStickerService.getStickerFormats();
    this.addComponentData(this.stickerFormatList$);

    this.dataSubscriptions.push(this.stickerFormatList$.subscribe(res => {
      this.stickerFormatList = res.data.stickerFormats;
    }));

    this.medicineList$ = this.medService.getMedicines(this.activeAdminUnitCuid$);
    this.addComponentData(this.medicineList$);

    this.dataSubscriptions.push(
      this.medicineList$.subscribe(res => {
        let med = [...res.data.medicines];
        this.medicineList = med.sort((m1: Medicine, m2: Medicine) => m1.label.localeCompare(m2.label));
        this.origMedicineList = this.medicineList;
      })
    );

    this.dragulaService.drag.subscribe(event => {
      let medCuid = event[1].id;
      this.loadPreviewImage(medCuid);
    });

  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.dragulaService.destroy('medicines');
  }

  loadPreviewImage(medicineId) {

    if (!medicineId) {
      return;
    }

    this.dataSubscriptions.push(this.medStickerService.generateStickerPreview(medicineId, this.printedSheetCuid$, this.stickerFormatCuid$)
      .subscribe((res: any) => {
        let newPreview: { [medicineCuid: string]: string } = {};
        this.previewImages = Object.assign(newPreview, this.previewImages, {[medicineId]: res.data.generateMedicineStickerPreview + "?" + AbstractVisbosControl.cuid()});
      }));
  }

  onPrintedSheetSelected(printedSheet: PrintedSheet) {
    this.control.get('printed_sheet_id').setValue(printedSheet.cuid);
    this.updatePageLayout(printedSheet);
  }

  updatePageLayout(printedSheet: PrintedSheet) {
    this.stickerPage.rows = printedSheet.rows;
    this.stickerPage.cols = printedSheet.columns;
    this.stickerPage.stickerWidth = printedSheet.sticker_width;
    this.stickerPage.stickerHeight = printedSheet.sticker_height;

    this.stickerPage.initLayout();
  }

  filterItems(searchTerm: string): Medicine[] {
    let result = this.origMedicineList;

    if (searchTerm && !(searchTerm == "") && this.origMedicineList) {
      result = this.origMedicineList.filter(med => {
        return (med.name ? med.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()) : false)
          || med.substances[0].substance.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase());
      });
    }

    return result;
  }

  searchFinished(result: Medicine[]) {
    this.medicineList = result;
  }

  searchCleared() {
    this.medicineList = this.origMedicineList;
  }

  createStickerSheet() {
    let data = Object.assign({}, this.control.value);

    let stickers = [];
    for (let r in this.stickerPage.cellContentContainer) {
      for (let c in this.stickerPage.cellContentContainer[r]) {
        let cell = this.stickerPage.cellContentContainer[r][c];
        if (cell) {
          let row: number = +r;
          let col: number = +c;
          stickers.push({medicine_id: cell.cuid, sheet_row: (row + 1), sheet_column: (col + 1)})
        }
      }
    }

    data.stickers = stickers;

    this.medStickerService.saveCustomStickersSheet(data).subscribe(
      res => {
        this.sessionActions.pushMessage("Druckbogen erfolgreich gespeichert!", MessageType.SUCCESS, "Druckbogen");
        this.router.navigate(['../'], {relativeTo: this.route});
      },
      err => this.sessionActions.throwError(err, "Fehler beim Speichern des Druckbogens!")
    );
  }

  resetContent() {
    this.stickerPage.resetContent();
  }

}



// WEBPACK FOOTER //
// ./src/app/medicine-stickers/components/custom-sticker-page-layout/custom-sticker-page-layout.component.ts